/** PostHog event name constants. Use these with usePostHogCapture().capture() so event names stay consistent and discoverable. */
export const POSTHOG_EVENTS = {
  LOGIN_SUCCESS: "login_success",
  LOGOUT: "logout",
  SIGNUP_SUCCESS: "signup_success",
  ASSET_GROUP_SELECTED: "asset_group_selected",
  ASSET_SELECTED: "asset_selected",
  ASSET_CREATED: "asset_created",
  ASSET_GEOMETRY_DOWNLOADED: "asset_geometry_downloaded",
  ASSET_GROUP_CREATED: "asset_group_created",
  ONBOARDING_COMPLETED: "onboarding_completed",
  WELCOME_START_CHAT: "welcome_start_chat",
  WELCOME_REQUEST_ACCESS: "welcome_request_access",
  ACCESS_REQUEST_SUBMITTED: "access_request_submitted",
  ACCESS_REQUEST_UPDATED: "access_request_updated",
  // Terms and Conditions events
  TERMS_ACCEPTED: "terms_accepted",
  TERMS_DECLINED: "terms_declined",
  // Chat Privacy Policy events
  CHAT_PRIVACY_ACCEPTED: "chat_privacy_accepted",
  CHAT_PRIVACY_DECLINED: "chat_privacy_declined",
  // Organization AI Analyst access events
  ORG_CHAT_ACCESS_REQUESTED: "org_chat_access_requested",
  ORG_CHAT_ACCESS_TOGGLED: "org_chat_access_toggled",
  // User Persona / Onboarding step events
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  PERSONA_CREATED: "persona_created",
  // Chat interaction events
  CHAT_MESSAGE_SENT: "chat_message_sent",
  CHAT_FILE_ATTACHED: "chat_file_attached",
  CHAT_MAP_GEOMETRY_ATTACHED: "chat_map_geometry_attached",
  CHAT_PINNED_PROMPT_SELECTED: "chat_pinned_prompt_selected",
  CHAT_RESPONSE_RATED_UP: "chat_response_rated_up",
  CHAT_RESPONSE_RATED_DOWN: "chat_response_rated_down",
  // Schedule events
  SCHEDULE_CREATED: "schedule_created",
  SCHEDULE_EDITED: "schedule_edited",
  SCHEDULE_DELETED: "schedule_deleted",
  SCHEDULE_RUN_TRIGGERED: "schedule_run_triggered",
  SCHEDULE_EMAIL_CONDITION_SET: "schedule_email_condition_set",
  // Onboarding checklist events
  CHECKLIST_OPENED: "checklist_opened",
  CHECKLIST_STEP_COMPLETED: "checklist_step_completed",
  CHECKLIST_HAT_UNLOCKED: "checklist_hat_unlocked",
  CHECKLIST_ADDRESS_SUBMITTED: "checklist_address_submitted",
  // Composed Forecast events
  COMPOSED_FORECAST_CREATED: "composed_forecast_created",
  COMPOSED_FORECAST_UPDATED: "composed_forecast_updated",
  COMPOSED_FORECAST_DELETED: "composed_forecast_deleted",
  COMPOSED_FORECAST_DUPLICATED: "composed_forecast_duplicated",
  COMPOSED_FORECAST_PREVIEW_EXPANDED: "composed_forecast_preview_expanded",
  // New-user tour events
  TOUR_STARTED: "tour_started",
  TOUR_STEP_VIEWED: "tour_step_viewed",
  TOUR_COMPLETED: "tour_completed",
  TOUR_SKIPPED: "tour_skipped",
  // Shared conversation events
  SHARE_LINK_CREATED: "share_link_created",
  SHARE_LINK_COPIED: "share_link_copied",
  SHARE_LINK_TOGGLED: "share_link_toggled",
  SHARE_LINK_DELETED: "share_link_deleted",
  SHARED_CONVERSATION_VIEWED: "shared_conversation_viewed",
  SHARED_SIGNUP_CLICKED: "shared_signup_clicked",
  SHARE_EMBED_CREATED: "share_embed_created",
  SHARE_EMBED_COPIED: "share_embed_copied",
  // Asset membership-tier limit events
  ASSET_LIMIT_REACHED: "asset_limit_reached",
  ASSET_LIMIT_UPGRADE_CLICKED: "asset_limit_upgrade_clicked",
  // Processed model membership-tier limit events
  MODEL_LIMIT_REACHED: "model_limit_reached",
  MODEL_LIMIT_UPGRADE_CLICKED: "model_limit_upgrade_clicked",
  // Model deploy proposal (AI chat) events
  MODEL_DEPLOY_APPROVED: "model_deploy_approved",
  MODEL_DEPLOY_DECLINED: "model_deploy_declined",
  // AI Analyst graph events
  GRAPH_TIMEZONE_TOGGLED: "graph_timezone_toggled",
  // Admin user-records explorer events
  ADMIN_USER_RECORDS_VIEWED: "admin_user_records_viewed",
  ADMIN_USER_RECORD_EXPANDED: "admin_user_record_expanded",
  ADMIN_USER_CONVERSATION_VIEWED: "admin_user_conversation_viewed",
  // Model schema + Model Garden events
  MODEL_RUN_QUEUED: "model_run_queued",
  MODEL_ACTIVE_TOGGLED: "model_active_toggled",
  MODEL_GARDEN_VIEWED: "model_garden_viewed",
  MODEL_GARDEN_PAGE_VIEWED: "model_garden_page_viewed",
  MODEL_TEMPLATE_SELECTED: "model_template_selected",
  MODEL_SCHEMA_EXPORTED: "model_schema_exported",
  MODEL_RUN_HISTORY_VIEWED: "model_run_history_viewed",
  // Inputs a V2 run was given, opened from the runs table or a forecast preview (source: run_table | forecast_preview)
  RUN_INPUTS_VIEWED: "run_inputs_viewed",
  // A model output file saved from the preview footer or a run's output list (source: forecast_preview | run_detail)
  MODEL_OUTPUT_FILE_DOWNLOADED: "model_output_file_downloaded",
  // Dataset upload events (web app uploads through the ingest pipeline)
  DATASET_UPLOAD_COMPLETED: "dataset_upload_completed",
  FORECAST_UPLOAD_COMPLETED: "forecast_upload_completed", // an upload into an external forecast model (UploadForecastModal)
  // Which tab of a dataset's detail view was opened (overview, uploads, processing)
  DATASET_DETAIL_TAB_VIEWED: "dataset_detail_tab_viewed",
  // Historical models (docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, stage 4): which tab of the model's detail was opened, and a run asked for from it (trigger: MANUAL | BACKFILL)
  HISTORICAL_MODEL_TAB_VIEWED: "historical_model_tab_viewed",
  HISTORICAL_MODEL_RUN_REQUESTED: "historical_model_run_requested",
  // Column mapping: the editor was opened from a "not mapped" flag (card | graph), Map / Map all was used in it, and a mapping was saved
  DATASET_COLUMN_MAPPING_OPENED: "dataset_column_mapping_opened",
  DATASET_COLUMNS_MAPPED: "dataset_columns_mapped",
  DATASET_COLUMN_MAPPING_SAVED: "dataset_column_mapping_saved",
  // Tools / forecast email events (the user-facing /home/tools and /home/email-schedule pages)
  TOOLS_PAGE_VIEWED: "tools_page_viewed",
  DELIVERY_LOGS_PAGE_VIEWED: "delivery_logs_page_viewed",
  FORECAST_EMAILS_PAGE_VIEWED: "forecast_emails_page_viewed",
  FORECAST_EMAIL_SCHEDULE_CREATED: "forecast_email_schedule_created",
  FORECAST_EMAIL_SCHEDULE_EDITED: "forecast_email_schedule_edited",
  FORECAST_EMAIL_SCHEDULE_DELETED: "forecast_email_schedule_deleted",
  FORECAST_EMAIL_SCHEDULE_TOGGLED: "forecast_email_schedule_toggled",
  FORECAST_EMAIL_TEST_SENT: "forecast_email_test_sent",
  // Watershed Delineation tool (/home/watershed-delineation): a basin was traced, saved, assigned to an asset or deleted
  // Forecast Verification Reporting tool (/home/forecast-verification): reports created, edited (re-run), re-run, deleted, exported
  FORECAST_VERIFICATION_PAGE_VIEWED: "forecast_verification_page_viewed",
  FORECAST_REPORT_CREATED: "forecast_report_created",
  FORECAST_REPORT_EDITED: "forecast_report_edited",
  FORECAST_REPORT_RERUN: "forecast_report_rerun",
  FORECAST_REPORT_DELETED: "forecast_report_deleted",
  FORECAST_REPORT_VIEWED: "forecast_report_viewed",
  FORECAST_REPORT_EXPORTED: "forecast_report_exported",
  // A report page opened from a link in the PDF (utm_source=erode_report); props: report_id, utm_content (which link)
  FORECAST_REPORT_OPENED_FROM_PDF: "forecast_report_opened_from_pdf",
  WATERSHED_TOOL_PAGE_VIEWED: "watershed_tool_page_viewed",
  WATERSHED_DELINEATED: "watershed_delineated",
  WATERSHED_DELINEATION_FAILED: "watershed_delineation_failed",
  WATERSHED_SAVED: "watershed_saved",
  WATERSHED_ASSIGNED: "watershed_assigned",
  WATERSHED_DELETED: "watershed_deleted",
  WATERSHED_RENAMED: "watershed_renamed",
  WATERSHED_SPLIT: "watershed_split",
  WATERSHED_SPLIT_FAILED: "watershed_split_failed",
  WATERSHED_SPLIT_SAVED: "watershed_split_saved",
  WATERSHED_COMBINED: "watershed_combined",
  WATERSHED_DOWNLOADED: "watershed_downloaded",
  // Group overview map: the view was opened, an asset was picked on the map or in the card's list, and a layer (polygon | line | point) was shown or hidden
  GROUP_OVERVIEW_VIEWED: "group_overview_viewed",
  GROUP_OVERVIEW_ASSET_SELECTED: "group_overview_asset_selected",
  GROUP_OVERVIEW_LAYER_TOGGLED: "group_overview_layer_toggled",
  // Email engagement events — emitted SERVER-SIDE by emailEngagementFunction (SES → SNS → PostHog), registered here so the catalog stays complete. Do not capture these from the frontend.
  EMAIL_OPENED: "email_opened",
  EMAIL_CLICKED: "email_clicked",
} as const;

export type PostHogEventName = (typeof POSTHOG_EVENTS)[keyof typeof POSTHOG_EVENTS];
